import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  ChevronLeft,
  FileSpreadsheet,
  Landmark,
  Lock,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useIsAdminTier, useIsReadOnly, useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { coincideBusqueda, prepararBusqueda } from '@/shared/lib/tablaOrden';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';
import { listMovimientos, type MovimientoRow } from './api';
import { canWriteCaja, isNovaCaja, isCajaDiferencia } from './lib/permissions';
import { CajaSelect } from './components/CajaSelect';
import { CajaNombre } from './components/CajaNombre';
import { cajaBase, esCajaEfectivoConPar, esCajaN, etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import { useEsCajaUsd } from '@/shared/lib/cajaMoneda';
import {
  DEFAULT_TESORERIA_FILTER,
  FiltrarPopover,
  type TesoreriaFilterValue,
  setsEqual,
} from './components/FiltrarPopover';
import { MovimientosTable } from './components/MovimientosTable';
import { ReclasificarCuentaDialog } from './components/ReclasificarCuentaDialog';
import { TrasladarCajaDialog } from './components/TrasladarCajaDialog';
import { ViewMovimientoDialog } from './components/ViewMovimientoDialog';
import { AgregarMovimientoDialog } from './components/AgregarMovimientoDialog';
import { CargosMercadoPagoDialog } from './components/CargosMercadoPagoDialog';
import { TransferenciaDialog } from './components/TransferenciaDialog';
import { CierreCajaDialog } from './components/CierreCajaDialog';

/**
 * Detalle de UNA caja (`/tesoreria/:caja`) para `11.MovimientoCajas`.
 * Mantiene el patrón de galería única de PowerApps Screen_Tesoreria
 * (Screen_Tesoreria.pa.yaml:638): filtros + tabla con saldo corrido,
 * alimentada por todas las filas Pendiente de la caja sin importar el mes.
 * El período activo es una tajada autocontenida — cuando corre un Cierre de
 * Caja las filas Pendiente previas pasan a Confirmado y salen de la vista.
 *
 * La caja sale de la URL (antes: un combo + localStorage). La puerta de
 * entrada ahora es el resumen en `/tesoreria`, así el back del navegador y
 * los links a una caja puntual funcionan.
 *
 * El saldo lo calcula el server (`tesoreriaHelpers.computeSaldoSeries`) con
 * semántica de Apertura-como-RESET. Ese helper es la única fuente de verdad
 * — el resumen usa exactamente el mismo, agrupado por caja.
 */

// Visibility for Nova in the caja dropdown matches the Nova write-eligible
// set: the three Nova-channel payment methods Nova actually operates
// (Nova Banorte, Nova Mercado Pago, Herconstru). Caja Diferencia is
// excluded — even though Nova margin lands there, it's Fornitalia's
// bookkeeping caja and Nova doesn't operate it. Everything else (BBVA
// Italmexy, Efectivo Pesos/Dolar, AR cajas, …) stays hidden. The
// shared `isNovaCaja` predicate in `./lib/permissions` is the single
// source of truth — it also drives the per-caja write gate below.

export function TesoreriaCajaPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useSession();
  const isNovaRole = profile?.perfil === 'nova';
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  // La caja viene de la URL. Los nombres tienen espacios ('Nova Mercado
  // Pago') así que el link va encodeado y acá se decodifica.
  const { caja: cajaParam } = useParams<{ caja: string }>();
  const caja = decodeURIComponent(cajaParam ?? '');
  const isReadOnly = useIsReadOnly();
  const isAdminTier = useIsAdminTier();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<TesoreriaFilterValue>(DEFAULT_TESORERIA_FILTER);
  const [viewing, setViewing] = useState<MovimientoRow | null>(null);
  const [agregarTipo, setAgregarTipo] = useState<'Ingreso' | 'Egreso' | null>(null);
  // Botón amarillo: cobro/pago de MercadoPago al que se le cargan los cargos
  // (comisión, IVA, retenciones…) como egresos hijos.
  const [impuestoParent, setImpuestoParent] = useState<MovimientoRow | null>(null);
  // Movimiento cuya cuenta contable se está reimputando.
  const [reclasificando, setReclasificando] = useState<MovimientoRow | null>(null);
  // Movimiento manual que se está pasando a otra caja de la misma moneda.
  const [trasladando, setTrasladando] = useState<MovimientoRow | null>(null);
  const [transferirKind, setTransferirKind] = useState<
    'Transferencia' | 'Deposito' | null
  >(null);
  const [cierreOpen, setCierreOpen] = useState(false);

  const esCajaUsd = useEsCajaUsd();
  const mediosQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'medios'],
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });

  // Nova role sees only Nova-channel cajas + Caja Diferencia (where
  // their margin lands — read-only since writes are emitted by the
  // server-side emitCajaDiferencia). Filtering happens here so the
  // dropdown, the default-pick logic, and any downstream consumers
  // all read the same scoped list.
  const visibleMedios = useMemo(() => {
    // La caja E-cheq no guarda plata: al acreditar, el cobro pasa al banco.
    const data = (mediosQ.data ?? []).filter((m) => m.fields.Tipo_MDP !== 'E-cheq');
    if (!isNovaRole) return data;
    return data.filter(
      (m) => isNovaCaja(m.fields.Medio_MDP) || isCajaDiferencia(m.fields.Medio_MDP),
    );
  }, [mediosQ.data, isNovaRole]);

  // Per-caja write gate. Drives every action button below — top toolbar
  // (Ingreso/Egreso/Transferir/Depositar), the Agregar Impuesto callback
  // on the movimientos table, and the Cierre de Caja footer button. The
  // server enforces the same rule on every mutation endpoint, so a
  // crafted client can't bypass it.
  const canWrite = useMemo(
    () => canWriteCaja(caja, profile?.perfil, countryCode),
    [caja, profile?.perfil, countryCode],
  );
  // Aviso para cuando un botón de carga se apaga por la CAJA elegida y no
  // por el rol — evita que el usuario crea que la pantalla se rompió.
  //
  // ⚠ Hoy no se muestra nunca: desde el 2026-08-20 los tres perfiles que
  // llegan a Tesorería (admin, administracion, nova) escriben en las cajas
  // Nova y en Caja Diferencia, así que `canWrite` siempre es true acá. Se
  // deja a propósito, enganchado y listo: el permiso de esas cajas ya se
  // movió tres veces (se le quitó a admin en 05/2026, se le dio a
  // administración en 08/2026, y ahora se abrió a todos), y cuando se
  // vuelva a restringir alcanza con tocar `canWriteCaja` en
  // `./lib/permissions` — este cartel se reactiva solo. Si agregás un caso
  // nuevo, sumale su rama de texto acá.
  // Destinos posibles para "Pasar a otra caja": misma moneda, con permiso de
  // escritura, sin la caja actual. El par de efectivo (con/sin factura) va
  // sugerido porque es el caso que pidió el cliente.
  const destinosTraslado = useMemo(() => {
    const esUsd = esCajaUsd(caja);
    return visibleMedios.filter((m) => {
      const nombre = m.fields.Medio_MDP ?? '';
      return (
        nombre !== '' &&
        nombre !== caja &&
        esCajaUsd(nombre) === esUsd &&
        canWriteCaja(nombre, profile?.perfil, countryCode)
      );
    });
  }, [visibleMedios, caja, profile?.perfil, countryCode, esCajaUsd]);
  const cajaPar = esCajaEfectivoConPar(caja)
    ? esCajaN(caja)
      ? cajaBase(caja)
      : `${caja} N`
    : undefined;

  const restrictedCajaReason = useMemo<string | null>(() => {
    if (!caja || canWrite || isNovaRole) return null;
    if (countryCode !== 'MX') return null;
    if (isNovaCaja(caja)) {
      return 'Sólo los roles Nova y Administración registran movimientos en esta caja.';
    }
    return null;
  }, [caja, canWrite, countryCode, isNovaRole]);

  // Caja que no existe, dada de baja, o fuera del alcance del rol (Nova
  // escribiendo la URL de una caja que no opera, o alguien volviendo con el
  // back después de cambiar de país): al resumen. El gate de lectura del
  // server no cambia — esto evita una pantalla vacía sin explicación.
  useEffect(() => {
    if (mediosQ.isPending || visibleMedios.length === 0) return;
    const names = new Set(
      visibleMedios.map((m) => m.fields.Medio_MDP ?? '').filter(Boolean),
    );
    if (!caja || !names.has(caja)) navigate('/tesoreria', { replace: true });
  }, [visibleMedios, mediosQ.isPending, caja, navigate]);

  // Multi-user gallery — refetch on tab refocus + every 15s so each
  // admin sees new movimientos posted from cobros/pagos/transferencias
  // running on other tabs without hitting Refrescar. Paired with the
  // server-side OCC guards (PreconditionFailedError) on the cierre
  // and movimiento write paths.
  const movimientosQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'movimientos', caja],
    queryFn: () => listMovimientos({ caja }),
    staleTime: 30_000,
    // ponytail: 60 s sin Realtime en cash_movements (egress); publicar la tabla si hace falta en vivo.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled: Boolean(caja),
  });

  const rows = movimientosQ.data?.rows ?? [];
  const totals = movimientosQ.data?.totals;
  const moneda = totals?.moneda ?? (esCajaUsd(caja) ? 'USD' : 'ARS');

  // Client-side filter on top of server response: Tipo, search, and a
  // hard hide of `Cierre de Caja` rows (they're prior-period bookkeeping
  // — the saldo math still includes them server-side, the user just
  // doesn't need them in the gallery).
  const filtered = useMemo(() => {
    const b = prepararBusqueda(search);
    return rows.filter((r) => {
      const f = r.fields;
      const tipo = f.TipoMovimiento_MC ?? '';
      if (tipo === 'Cierre de Caja') return false;
      if (tipo && !filter.tiposMovimiento.has(tipo as never)) return false;
      if (!b) return true;
      return (
        // El Id se busca exacto: "12" no tiene que traer 10001234.
        r.id === b.texto.replace(/^#/, '') ||
        // Todo lo que se ve en la fila, importes y saldo incluidos.
        coincideBusqueda(
          [
            tipo,
            tipo === 'Egreso' ? 'Gasto' : '',
            f.Fecha_MC,
            f.Hora_MC,
            f.Descripcion_MC,
            f.CatDesc_MC,
            f.Cliente_MC,
            f.Categoria_MC,
            f.CuentaContable_MC,
            f.MedioPago_MC,
            f.Status_MC,
            f.Observaciones_MC,
            parseMoney(f.MontoNeto_MC),
            r.saldoAt,
          ],
          b,
        )
      );
    });
  }, [rows, filter.tiposMovimiento, search]);

  // Footer totals reflect the server's month-wide calc (unfiltered),
  // not the client-filtered visible list — that matches PowerApps where
  // the filter toggles narrow visibility but totals always show the
  // real cash position for the caja.
  const ingresos = totals?.ingresos ?? 0;
  const egresos = totals?.egresos ?? 0;
  const saldoActual = totals?.saldoActual ?? 0;

  const filterActive = !setsEqual(
    filter.tiposMovimiento,
    DEFAULT_TESORERIA_FILTER.tiposMovimiento,
  );

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Title, 4 action buttons,
          CajaSelect, Search, Filter, Excel, Refresh). On mobile, CSS
          `order` classes plus `basis-full` break dividers split the
          single-row desktop layout into 4 stacked rows:
            Row 1: Title · Refresh
            Row 2: Ingreso / Egreso / Transferir / Depositar (icon-only)
            Row 3: CajaSelect · Excel (icon)
            Row 4: Búsqueda · Filtrar
       */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <div className="mr-auto flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => navigate('/tesoreria')}
            aria-label="Volver al resumen de cajas"
            title="Volver al resumen de cajas"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </Button>
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate('/tesoreria')}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Tesorería
            </button>
            <h1 className="truncate text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              {caja ? <CajaNombre caja={caja} className="max-w-full [&>svg]:h-6 [&>svg]:w-6" /> : 'Tesorería'}
            </h1>
          </div>
        </div>
        {/* Two semantic groups: create-movement (Ingreso/Egreso, filled
            black) and transfer-between-cajas (Transferir/Depositar,
            outline). On mobile they sit at opposite ends of the row so
            the visible gap reads as intentional separation rather than
            stray whitespace; on desktop the inner gap collapses and
            both groups sit flush together as before. */}
        {!isReadOnly && canWrite && (
          <div className="order-3 flex w-full items-center justify-between gap-2 sm:order-none sm:w-auto sm:justify-start sm:gap-1">
            <div className="flex gap-1">
              <Button
                onClick={() => setAgregarTipo('Ingreso')}
                disabled={!caja}
                className="h-12 w-12 shrink-0 gap-0 bg-foreground p-0 text-background hover:bg-foreground/90 sm:w-auto sm:gap-2 sm:px-4"
                aria-label="Registrar un ingreso manual"
                title="Registrar un ingreso manual"
              >
                <ArrowUpCircle className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Ingreso</span>
              </Button>
              <Button
                onClick={() => setAgregarTipo('Egreso')}
                disabled={!caja}
                className="h-12 w-12 shrink-0 gap-0 bg-foreground p-0 text-background hover:bg-foreground/90 sm:w-auto sm:gap-2 sm:px-4"
                aria-label="Registrar un egreso manual"
                title="Registrar un egreso manual"
              >
                <ArrowDownCircle className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Egreso</span>
              </Button>
            </div>
            <div className="flex gap-1">
              <Button
                onClick={() => setTransferirKind('Transferencia')}
                disabled={!caja}
                variant="outline"
                className="h-12 w-12 shrink-0 gap-0 p-0 sm:w-auto sm:gap-2 sm:px-4"
                aria-label="Transferir entre cuentas"
                title="Transferir entre cuentas"
              >
                <ArrowLeftRight className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Transferir</span>
              </Button>
              <Button
                onClick={() => setTransferirKind('Deposito')}
                disabled={!caja}
                variant="outline"
                className="h-12 w-12 shrink-0 gap-0 p-0 sm:w-auto sm:gap-2 sm:px-4"
                aria-label="Depositar entre cuentas"
                title="Depositar entre cuentas"
              >
                <Landmark className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Depositar</span>
              </Button>
            </div>
          </div>
        )}
        <div className="order-5 min-w-[10rem] flex-1 sm:order-none sm:flex-initial">
          {/* Cambiador rápido: quien vive en Tesorería salta de caja sin
              pasar por el resumen. Ahora navega en vez de setear estado. */}
          <CajaSelect
            value={caja}
            onChange={(v) => navigate(`/tesoreria/${encodeURIComponent(v)}`)}
            medios={visibleMedios}
            loading={mediosQ.isPending}
          />
        </div>
        <div className="relative order-8 min-w-[10rem] flex-1 sm:order-none sm:w-56 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        <div className="order-9 sm:order-none">
          <FiltrarPopover
            value={filter}
            onChange={setFilter}
            onReset={() => setFilter(DEFAULT_TESORERIA_FILTER)}
            active={filterActive}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="order-6 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => void exportMovimientosToXlsx(filtered, caja, moneda)}
          disabled={filtered.length === 0}
          aria-label="Exportar a Excel"
          title="Exportar a Excel"
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => qc.invalidateQueries({ queryKey: ['tesoreria'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', movimientosQ.isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
        {/* Mobile-only row breaks. Sit between visual rows in the
            order sequence so wrap-after-N is enforced on narrow widths
            and disappears entirely (display:none) at sm+. */}
        <div className="order-2 basis-full sm:hidden" aria-hidden />
        <div className="order-4 basis-full sm:hidden" aria-hidden />
        <div className="order-7 basis-full sm:hidden" aria-hidden />
      </div>

      {restrictedCajaReason && (
        <div className="flex shrink-0 items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{restrictedCajaReason}</span>
        </div>
      )}

      <MovimientosTable
        rows={filtered}
        moneda={moneda}
        loading={movimientosQ.isPending}
        error={movimientosQ.isError}
        searchActive={Boolean(search.trim()) || filterActive}
        onView={(row) => setViewing(row)}
        onAgregarImpuesto={
          isReadOnly || !canWrite ? undefined : (row) => setImpuestoParent(row)
        }
        onReclasificar={
          // Reimputar es contabilidad, no caja: sólo admin/administración.
          // `canWrite` ya cubre el permiso sobre ESTA caja.
          isReadOnly || !canWrite || !isAdminTier
            ? undefined
            : (row) => setReclasificando(row)
        }
        onTrasladar={isReadOnly || !canWrite ? undefined : (row) => setTrasladando(row)}
        onRetry={() => movimientosQ.refetch()}
      />

      <FooterTotals
        ingresos={ingresos}
        egresos={egresos}
        saldo={saldoActual}
        moneda={moneda}
        rowCount={filtered.length}
        onCerrar={() => setCierreOpen(true)}
        cerrarDisabled={!caja || movimientosQ.isPending}
        canCerrar={canWrite}
      />

      {viewing && (
        <ViewMovimientoDialog
          row={viewing}
          moneda={moneda}
          onClose={() => setViewing(null)}
          onMutated={() => {
            qc.invalidateQueries({ queryKey: ['tesoreria'] });
            setViewing(null);
          }}
        />
      )}
      {agregarTipo && (
        <AgregarMovimientoDialog
          tipo={agregarTipo}
          caja={caja}
          moneda={moneda}
          onClose={() => setAgregarTipo(null)}
        />
      )}
      {impuestoParent && (
        <CargosMercadoPagoDialog
          caja={caja}
          moneda={moneda}
          parent={impuestoParent}
          onClose={() => setImpuestoParent(null)}
        />
      )}
      {reclasificando && (
        <ReclasificarCuentaDialog
          mov={{
            id: reclasificando.id,
            categoria: reclasificando.fields.Categoria_MC ?? null,
            cuentaContable: reclasificando.fields.CuentaContable_MC ?? null,
            descripcion: reclasificando.fields.Descripcion_MC ?? null,
            fecha: reclasificando.fields.Fecha_MC ?? null,
            tipo: reclasificando.fields.TipoMovimiento_MC ?? null,
            montoTexto: formatMoney(
              parseMoney(reclasificando.fields.MontoNeto_MC) ?? 0,
              moneda,
            ),
            contexto: etiquetaCaja(caja),
          }}
          onClose={() => setReclasificando(null)}
          onDone={() => movimientosQ.refetch()}
        />
      )}
      {trasladando && (
        <TrasladarCajaDialog
          row={trasladando}
          cajaOrigen={caja}
          moneda={moneda}
          destinos={destinosTraslado}
          sugerida={
            cajaPar && destinosTraslado.some((m) => m.fields.Medio_MDP === cajaPar) ? cajaPar : undefined
          }
          onClose={() => setTrasladando(null)}
        />
      )}
      {transferirKind && (
        <TransferenciaDialog
          kind={transferirKind}
          origen={caja}
          origenCurrency={moneda}
          medios={mediosQ.data ?? []}
          onClose={() => setTransferirKind(null)}
        />
      )}
      {cierreOpen && (
        <CierreCajaDialog
          caja={caja}
          moneda={moneda}
          onClose={() => setCierreOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterTotals({
  ingresos,
  egresos,
  saldo,
  moneda,
  rowCount,
  onCerrar,
  cerrarDisabled,
  canCerrar,
}: {
  ingresos: number;
  egresos: number;
  saldo: number;
  moneda: 'ARS' | 'MXN' | 'USD';
  rowCount: number;
  onCerrar: () => void;
  cerrarDisabled: boolean;
  /** True iff the selected caja is one the user can mutate. Cierre de
   *  Caja is itself a write — pass `canWriteCaja(...)` from the parent. */
  canCerrar: boolean;
}) {
  const isReadOnly = useIsReadOnly();
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
      <span className="text-xs text-muted-foreground">
        {rowCount} movimiento{rowCount === 1 ? '' : 's'} visibles
      </span>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Pill label="Ingresos">{formatMoney(ingresos, moneda)}</Pill>
        <Pill label="Egresos">{formatMoney(egresos, moneda)}</Pill>
        <Pill
          label="Saldo actual"
          emphasize
          tone={saldo < 0 ? 'negative' : 'neutral'}
        >
          {formatMoney(saldo, moneda)}
        </Pill>
        {!isReadOnly && canCerrar && (
          <Button
            onClick={onCerrar}
            disabled={cerrarDisabled}
            className="h-9 gap-2 bg-foreground text-background hover:bg-foreground/90"
            title="Cierre de Caja (arqueo)"
          >
            <Lock className="h-4 w-4" aria-hidden />
            Cierre de Caja
          </Button>
        )}
      </div>
    </div>
  );
}

function Pill({
  label,
  children,
  emphasize,
  tone,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
  tone?: 'neutral' | 'negative';
}) {
  return (
    <span
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3',
        emphasize ? 'border-foreground/60 bg-foreground/5' : 'bg-secondary',
        tone === 'negative' && 'border-rose-300 bg-rose-50',
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          'font-semibold',
          tone === 'negative' ? 'text-rose-700' : 'text-foreground',
        )}
      >
        {children}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// XLSX export — opens natively in Excel. Reflects the currently-visible
// filtered rows so the file matches what the admin sees on screen.
// Crédito / Débito / Saldo are written as real numbers so Excel can sum
// them; the in-app Spanish formatting belongs to the gallery view, not
// the spreadsheet.
// ---------------------------------------------------------------------------

async function exportMovimientosToXlsx(
  rows: MovimientoRow[],
  caja: string,
  moneda: 'ARS' | 'MXN' | 'USD',
): Promise<void> {
  const headers = [
    'Tipo',
    'Id',
    'Fecha',
    'Hora',
    'Categoría',
    'Cuenta Contable',
    'Descripción',
    'Cliente',
    'Crédito',
    'Débito',
    `Saldo (${moneda})`,
    'Observaciones',
  ];
  const data: Array<Array<string | number | null>> = rows.map((r) => {
    const f = r.fields;
    const tipo = f.TipoMovimiento_MC ?? '';
    const monto =
      Number((f.MontoNeto_MC ?? '0').replace(/\./g, '').replace(',', '.')) || 0;
    const isApertura =
      tipo === 'Apertura de Caja' || (f.Categoria_MC ?? '') === 'Apertura';
    const isIngreso = !isApertura && tipo === 'Ingreso';
    const isEgreso = tipo === 'Egreso';
    return [
      tipo,
      Number(r.id) || r.id,
      f.Fecha_MC ?? '',
      f.Hora_MC ?? '',
      f.Categoria_MC ?? '',
      f.CuentaContable_MC ?? '',
      f.Descripcion_MC ?? f.CatDesc_MC ?? '',
      f.Cliente_MC ?? '',
      isIngreso ? monto : null,
      isEgreso ? monto : null,
      r.saldoAt,
      f.Observaciones_MC ?? '',
    ];
  });

  const today = fechaArchivoHoy();
  const slug = etiquetaCaja(caja).replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  await downloadXlsx({
    filename: `tesoreria_${slug}_${today}.xlsx`,
    sheetName: etiquetaCaja(caja).slice(0, 31), // Excel sheet name max 31 chars
    headers,
    rows: data,
  });
}
